import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import {
    collection,
    doc,
    getCountFromServer,
    getDoc,
    getDocs,
    initializeFirestore,
    limit,
    orderBy,
    persistentLocalCache,
    persistentMultipleTabManager,
    query,
    where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import config from 'constants/config';
import { toast } from 'react-toastify';
import { store } from 'redux/store';
import { formatDateValues } from './formatDateValues';
import { ITEMS_PER_PAGE } from 'constants/global';

const app = initializeApp(config.FB);
export const db = initializeFirestore(app, {
    localCache: persistentLocalCache(/*settings*/ { tabManager: persistentMultipleTabManager() }),
});
getAnalytics(app);
export const auth = getAuth(app);
export const storage = getStorage(app);

export const uploadFile = async (filePathAndName, file, setReturnType, setProgressFunc, setUploadTaskInstance) => {
    const storageRef = ref(storage, filePathAndName);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
        'state_changed',
        (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setProgressFunc && setProgressFunc(Math.floor(progress));
        },
        (error) => {
            error.code !== 'storage/canceled' && toast.error(error.message + '. Please reupload ' + file.name);
            setReturnType('error');
        },
        () => {
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => setReturnType(downloadURL));
        }
    );

    setUploadTaskInstance && setUploadTaskInstance(uploadTask);
};

export const fetchCollectionToRedux = async (
    collectionPath,
    setCollection,
    setLoading,
    limitCount = ITEMS_PER_PAGE
) => {
    try {
        setLoading && setLoading(true);
        let q = query(collection(db, collectionPath), orderBy('createdAt', 'desc'));
        if (limitCount) {
            q = query(q, limit(limitCount));
        }
        const collectionSnapshot = await getDocs(q);
        const dataArr = [];
        collectionSnapshot.forEach((doc) => {
            dataArr.push({
                ...doc.data(),
                ...formatDateValues(doc.data()),
            });
        });

        collectionSnapshot.size && store.dispatch(setCollection(dataArr));
        setLoading && setLoading(false);
    } catch (error) {
        setLoading && setLoading(false);
    }
};

export const modifyAuthError = (firebaseString) => {
    const removedChars = firebaseString.replace(/[/-]/g, ' ');
    const updatedString = removedChars
        .split(' ')
        .filter((word, idx) => idx)
        .join(' ');
    return 'Request failed. ' + updatedString.charAt(0).toUpperCase() + updatedString.slice(1) + '!';
};

export const fetchCollectionCount = async (collectionPath, setCount, showError) => {
    try {
        const collectionSnapshot = await getCountFromServer(collection(db, collectionPath));
        const count = collectionSnapshot.data().count;
        count && setCount(count);
    } catch (error) {
        showError &&
            error?.code !== 'unavailable' &&
            toast.error(`Error fetching total number of ${collectionPath}. Please reload the page.`);
    }
};

export const fetchCollectionCountByQuery = async (collectionQuery, setCount, setLoading, showError) => {
    try {
        setLoading && setLoading(true);
        const collectionSnapshot = await getCountFromServer(collectionQuery);
        const count = collectionSnapshot.data().count;
        setCount(count);
    } catch (error) {
        if (showError && error?.code !== 'unavailable') {
            toast.error(error?.message);
        }
    } finally {
        setLoading && setLoading(false);
    }
};

export const fetchCollectionByQuery = async (collectionQuery, setLoading, setCollection) => {
    setLoading && setLoading(true);
    const dataArr = [];
    try {
        const dataArrRef = await getDocs(collectionQuery);
        dataArrRef.forEach((doc) =>
            dataArr.push({
                ...doc.data(),
                ...formatDateValues(doc.data()),
            })
        );
        store.dispatch(setCollection(dataArr));
    } catch (error) {
        toast.error(error?.message);
    } finally {
        setLoading && setLoading(false);
    }
};

export const fetchCollectionByQueryLocal = async (
    collectionQuery,
    setLoading,
    setData,
    /** @type {boolean|string} */ showError,
    forceSetData,
    throwError
) => {
    setLoading && setLoading(true);
    const dataArr = [];
    try {
        const dataArrRef = await getDocs(collectionQuery);
        dataArrRef.forEach((doc) =>
            dataArr.push({
                ...doc.data(),
                ...formatDateValues(doc.data()),
            })
        );
        (dataArrRef.size || forceSetData) && setData && setData(dataArr);
        if (!setData) return dataArr;
    } catch (error) {
        if (showError && error?.code !== 'unavailable') {
            toast.error(typeof showError === 'string' ? showError : error?.message);
        }
        if (throwError || !setData) throw error;
    } finally {
        setLoading && setLoading(false);
    }
};

export const fetchCollectionWithPagination = async (
    collectionQuery,
    setLoading,
    setData,
    setLastDocRef,
    /** @type {boolean|string} */ showError
) => {
    setLoading && setLoading(true);
    const dataArr = [];
    try {
        const dataArrRef = await getDocs(collectionQuery);
        dataArrRef.forEach((doc) =>
            dataArr.push({
                ...doc.data(),
                ...formatDateValues(doc.data()),
            })
        );
        setLastDocRef(dataArrRef.docs[dataArrRef.docs.length - 1]);
        dataArrRef.size && setData(dataArr);
    } catch (error) {
        if (showError && error?.code !== 'unavailable' && error?.message !== 'nextPageData is not iterable') {
            toast.error(typeof showError === 'string' ? showError : error?.message);
        }
    } finally {
        setLoading && setLoading(false);
    }
};

export const fetchDocument = async (
    documentPath,
    documentId,
    setLoading,
    setData,
    /** @type {boolean|string} */ showError
) => {
    setLoading && setLoading(true);

    try {
        const docRef = doc(db, documentPath, documentId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            setData({
                ...docSnap.data(),
                ...formatDateValues(docSnap.data()),
            });
        }
    } catch (error) {
        if (showError && error?.code !== 'unavailable') {
            toast.error(typeof showError === 'string' ? showError : error?.message);
        }
    } finally {
        setLoading && setLoading(false);
    }
};

/**
 * Fetches documents where the specified field matches any of the provided values.
 * Automatically handles Firestore's 30-value limit for IN queries by chunking the values.
 * @param {string} collectionPath - Firestore collection path
 * @param {string} field - Document field to query against
 * @param {Array} values - Values to search for in the specified field
 * @param {Function} [setData] - Success callback that receives the results
 * @param {Function} [setLoading] - Loading state setter (optional)
 * @param {Array<{field: string, operator: import('firebase/firestore').WhereFilterOp, value: any}>} [additionalWhere=[]] - Array of additional where clauses to apply to the query
 * @param {boolean|string} [showError] - If true, shows default error toast. If string, uses it as custom error message
 * @param {boolean} [forceSetData=false] - If true, calls setData even with empty results
 * @returns {Promise<void>}
 */
export const fetchWithInQuery = async (
    collectionPath,
    field,
    values,
    setData,
    setLoading,
    additionalWhere = [],
    showError,
    forceSetData
) => {
    const FIRESTORE_IN_QUERY_LIMIT = 30;

    if (!values || !values.length) return;
    const uniqueValues = [...new Set(values)].filter(Boolean);

    if (!uniqueValues.length) return;

    const chunks = [];
    for (let i = 0; i < uniqueValues.length; i += FIRESTORE_IN_QUERY_LIMIT) {
        chunks.push(uniqueValues.slice(i, i + FIRESTORE_IN_QUERY_LIMIT));
    }

    setLoading && setLoading(true);
    try {
        const queryPromises = chunks.map((chunk) => {
            const conditions = [where(field, 'in', chunk)];

            additionalWhere.forEach(({ field: whereField, operator, value }) => {
                conditions.push(where(whereField, operator, value));
            });

            const q = query(collection(db, collectionPath), ...conditions);
            return getDocs(q);
        });

        const querySnapshots = await Promise.all(queryPromises);

        const results = [];
        const seen = new Set();

        querySnapshots.forEach((snapshot) => {
            snapshot.forEach((docSnap) => {
                if (!seen.has(docSnap.id)) {
                    seen.add(docSnap.id);
                    results.push({
                        ...docSnap.data(),
                        ...formatDateValues(docSnap.data()),
                    });
                }
            });
        });

        (results.length || forceSetData) && setData(results);
    } catch (error) {
        if (showError && error?.code !== 'unavailable') {
            toast.error(typeof showError === 'string' ? showError : error?.message);
        }
    } finally {
        setLoading && setLoading(false);
    }
};

export const fetchAnalytics = async (setAnalytics) => {
    const dataArrRef = await getDocs(collection(db, 'analytics'));
    const dataArr = [];
    dataArrRef.forEach((doc) => dataArr.push([doc.id, doc.data()]));
    const sectionsTotal = dataArr
        .filter(
            ([key, value]) =>
                !['contributionMargins', 'operatingIncome', 'locationsPrice', 'locationsCM2'].includes(key)
        )
        .map(([key, value]) => [key, Object.values(value).reduce((p, c) => p + c, 0)]);

    setAnalytics({
        ...Object.fromEntries(sectionsTotal),
        defaultData: Object.fromEntries(dataArr),
    });
};
